import { useState } from 'react';
import styled from 'styled-components';
import { Card } from '@shared/ui/Card';
import { Badge } from '@shared/ui/Badge';
import { Select } from '@shared/ui/Input';
import { useGetAssistantFeedbackQuery } from '@shared/api';
import type { AssistantDislikedItem } from '@entities/assistant/types';

const Toolbar = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing(4)};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 13px;
`;

const PeriodSelect = styled(Select)`
  width: 180px;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(6)};
`;

const MetricCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
`;

const MetricLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const MetricValue = styled.div`
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const MetricCaption = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(1)};
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SectionHint = styled.p`
  margin: 0 0 ${({ theme }) => theme.spacing(4)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(3)};
`;

const ItemCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(2.5)};
`;

const Meta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  align-items: center;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Label = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${({ theme }) => theme.colors.text.muted};
  margin-bottom: ${({ theme }) => theme.spacing(1)};
`;

const Text = styled.div`
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Comment = styled(Text)`
  padding: ${({ theme }) => `${theme.spacing(2)} ${theme.spacing(3)}`};
  border-left: 3px solid ${({ theme }) => theme.colors.status.danger};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const Answer = styled.details`
  font-size: 14px;

  summary {
    cursor: pointer;
    color: ${({ theme }) => theme.colors.text.secondary};
    font-size: 13px;
  }
  &[open] summary {
    margin-bottom: ${({ theme }) => theme.spacing(2)};
  }
`;

const Empty = styled.div`
  padding: ${({ theme }) => theme.spacing(8)};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const PERIODS = [7, 30, 90] as const;

function percent(part: number, whole: number): string {
  return whole > 0 ? `${Math.round((part / whole) * 100)}%` : '—';
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('ru-RU', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function DislikedCard({ item }: { item: AssistantDislikedItem }) {
  return (
    <ItemCard>
      <Meta>
        <span>{formatDateTime(item.answered_at)}</span>
        {item.section && <Badge $tone="info">{item.section}</Badge>}
        {item.roles.map((r) => (
          <Badge key={r} $tone="neutral">
            {r}
          </Badge>
        ))}
      </Meta>
      <div>
        <Label>Вопрос</Label>
        <Text>{item.question ?? 'Вопрос не найден (чат удалён частично)'}</Text>
      </div>
      {item.comment && (
        <div>
          <Label>Что не так — со слов сотрудника</Label>
          <Comment>{item.comment}</Comment>
        </div>
      )}
      <Answer>
        <summary>Ответ Помощника</summary>
        <Text>{item.answer}</Text>
      </Answer>
    </ItemCard>
  );
}

/**
 * «Администрация → Помощник»: как сотрудники оценивают ответы Помощника и какие
 * ответы отмечены «не полезно». По этим ответам правят справку Помощника
 * (`backend/crates/osnova-agents/src/assistant/help/*.md`). Кто спрашивал —
 * только ролями, без имени.
 */
export function AdminAssistantTab() {
  const [days, setDays] = useState<number>(30);
  const { data, isLoading, isError } = useGetAssistantFeedbackQuery(days);
  const report = data?.report;
  const rated = report ? report.helpful + report.not_helpful : 0;

  return (
    <>
      <Toolbar>
        <PeriodSelect
          value={days}
          aria-label="Период"
          onChange={(e) => setDays(Number(e.target.value))}
        >
          {PERIODS.map((p) => (
            <option key={p} value={p}>
              За {p} дней
            </option>
          ))}
        </PeriodSelect>
      </Toolbar>

      {isError && <Empty>Не удалось загрузить оценки.</Empty>}
      {isLoading && <Empty>Загружаем…</Empty>}

      {report && (
        <>
          <Grid>
            <MetricCard>
              <MetricLabel>Ответов</MetricLabel>
              <MetricValue>{report.answers}</MetricValue>
              <MetricCaption>дал Помощник за период</MetricCaption>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Оценено</MetricLabel>
              <MetricValue>{rated}</MetricValue>
              <MetricCaption>{percent(rated, report.answers)} ответов</MetricCaption>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Полезно</MetricLabel>
              <MetricValue>{percent(report.helpful, rated)}</MetricValue>
              <MetricCaption>
                {report.helpful} из {rated} оценённых
              </MetricCaption>
            </MetricCard>
            <MetricCard>
              <MetricLabel>Не полезно</MetricLabel>
              <MetricValue>{report.not_helpful}</MetricValue>
              <MetricCaption>{percent(report.not_helpful, rated)} оценённых</MetricCaption>
            </MetricCard>
          </Grid>

          <SectionTitle>Ответы «не полезно»</SectionTitle>
          <SectionHint>
            Свежие сверху. Обычно причина — справка Помощника устарела или в ней нет раздела: такие
            ответы передайте разработчикам.
          </SectionHint>
          {report.disliked.length === 0 ? (
            <Empty>За период нет ответов с отметкой «не полезно».</Empty>
          ) : (
            <List>
              {report.disliked.map((item) => (
                <DislikedCard key={item.answer_id} item={item} />
              ))}
            </List>
          )}
        </>
      )}
    </>
  );
}
